import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { fetchProducts } from "@/lib/api";
import ProductCard from "@/components/ProductCard";
import Reveal from "@/components/Reveal";
import { communityImages, journalArticles, lookbookImages } from "@/lib/translations";

const HERO_IMAGE = "https://images.pexels.com/photos/14760649/pexels-photo-14760649.jpeg";

export default function Home() {
  const { lang, t } = useLang();
  const [products, setProducts] = useState([]);
  const [parallax, setParallax] = useState(0);

  useEffect(() => {
    fetchProducts({ featured: true }).then(setProducts).catch(() => setProducts([]));
  }, []);

  useEffect(() => {
    const onScroll = () => setParallax(window.scrollY * 0.35);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div data-testid="home-page" className="bg-maly-cream text-maly-navy">
      {/* === HERO === */}
      <section data-testid="hero-section" className="relative h-screen min-h-[720px] overflow-hidden">
        <div
          className="absolute inset-0 will-change-transform"
          style={{ transform: `translateY(${parallax}px) scale(1.05)` }}
        >
          <img
            src={HERO_IMAGE}
            alt="MALY golf fashion"
            className="w-full h-full object-cover animate-hero-zoom"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-maly-navy/40 via-maly-navy/10 to-maly-navy/55" />
        </div>

        <div className="relative h-full flex flex-col justify-end pb-24 lg:pb-32 text-white">
          <div className="max-w-[1600px] mx-auto w-full px-6 lg:px-12">
            <div className="max-w-3xl">
              <div data-testid="hero-overline" className="font-body text-[11px] tracking-[0.32em] uppercase opacity-90 mb-8 animate-fade-up">
                {t.hero.overline}
              </div>
              <h1
                data-testid="hero-title"
                className="font-display text-[clamp(3rem,9vw,8rem)] leading-[0.92] tracking-[-0.02em] font-light animate-fade-up delay-200"
              >
                {t.hero.title1}
                <br />
                <span className="italic font-serif font-light">{t.hero.title2}</span>
              </h1>
              <p className="font-body text-base lg:text-lg max-w-xl mt-8 opacity-90 leading-relaxed animate-fade-up delay-400">
                {t.hero.sub}
              </p>
              <div className="flex flex-wrap items-center gap-4 mt-12 animate-fade-up delay-500">
                <Link
                  to="/shop"
                  data-testid="hero-cta-shop"
                  className="inline-flex items-center gap-3 bg-maly-cream text-maly-navy px-10 py-4 font-body text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-white transition-all duration-500 hover:translate-y-[-2px]"
                >
                  {t.hero.cta} <ArrowRight size={14} />
                </Link>
                <Link
                  to="/lookbook"
                  data-testid="hero-cta-lookbook"
                  className="inline-flex items-center gap-3 border border-white/60 px-10 py-4 font-body text-[11px] tracking-[0.25em] uppercase font-medium hover:bg-white hover:text-maly-navy transition-all duration-500"
                >
                  {t.hero.secondaryCta}
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 right-12 hidden lg:flex items-center gap-3 text-white font-body text-[10px] tracking-[0.3em] uppercase opacity-70 rotate-90 origin-bottom-right">
          <span>Scroll</span>
          <div className="w-12 h-px bg-white" />
        </div>
      </section>

      {/* === MARQUEE === */}
      <section data-testid="marquee" className="bg-maly-navy text-maly-cream py-8 overflow-hidden">
        <div className="marquee">
          <div className="marquee-track gap-16 px-8">
            {[...t.marquee, ...t.marquee, ...t.marquee, ...t.marquee].map((m, i) => (
              <div key={i} className="flex items-center gap-16">
                <span className="font-display text-2xl italic font-light whitespace-nowrap">{m}</span>
                <span className="w-2 h-2 bg-maly-gold rounded-full opacity-80" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* === MANIFESTO === */}
      <section data-testid="manifesto" className="py-32 lg:py-48 px-6 lg:px-12">
        <div className="max-w-[1400px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-24 items-center">
          <Reveal className="lg:col-span-5">
            <div className="aspect-[3/4] bg-maly-creamDeep img-zoom">
              <img
                src="https://images.unsplash.com/photo-1695418423426-b1e647262e73"
                alt="Manifesto"
                className="w-full h-full object-cover"
              />
            </div>
          </Reveal>
          <div className="lg:col-span-7 lg:pl-12">
            <Reveal>
              <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-8">
                {t.manifesto.overline}
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.01em] font-light">
                {t.manifesto.headline}
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="font-body text-base lg:text-lg leading-[1.8] opacity-75 mt-10 max-w-xl">
                {t.manifesto.body}
              </p>
            </Reveal>
            <Reveal delay={300}>
              <Link
                to="/about"
                data-testid="manifesto-cta"
                className="inline-flex items-center gap-3 mt-10 font-body text-[11px] tracking-[0.25em] uppercase font-medium link-underline"
              >
                {t.manifesto.cta} <ArrowRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* === FEATURED PRODUCTS === */}
      <section data-testid="featured-products" className="py-32 lg:py-40 px-6 lg:px-12 bg-white">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16 lg:mb-24">
            <div className="max-w-2xl">
              <Reveal>
                <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-6">
                  {t.featured.overline}
                </div>
              </Reveal>
              <Reveal delay={100}>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.01em] leading-[1.02] font-light">
                  {t.featured.headline}
                </h2>
              </Reveal>
              <Reveal delay={200}>
                <p className="font-body text-base opacity-70 mt-6 max-w-md leading-relaxed">{t.featured.sub}</p>
              </Reveal>
            </div>
            <Reveal delay={300}>
              <Link to="/shop" data-testid="featured-view-all" className="font-body text-[11px] tracking-[0.25em] uppercase font-medium link-underline whitespace-nowrap">
                {t.common.shopCollection} →
              </Link>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-10">
            {products.map((p, i) => (
              <Reveal key={p.id} delay={i * 80}>
                <ProductCard product={p} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* === LOOKBOOK CTA === */}
      <section data-testid="lookbook-cta" className="relative py-32 lg:py-48 overflow-hidden">
        <div className="max-w-[1600px] mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-8 lg:gap-12">
          <Reveal className="lg:col-span-5 lg:pt-24">
            <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-6">
              {t.lookbookCta.overline}
            </div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.01em] font-light">
              {t.lookbookCta.headline}
            </h2>
            <p className="font-body text-base lg:text-lg opacity-70 mt-8 max-w-md leading-relaxed">{t.lookbookCta.sub}</p>
            <Link to="/lookbook" data-testid="lookbook-cta-link" className="btn-primary mt-10">
              {t.lookbookCta.cta} <ArrowRight size={14} />
            </Link>
          </Reveal>

          <div className="lg:col-span-7 grid grid-cols-6 grid-rows-6 gap-4 h-[560px] lg:h-[680px]">
            <Reveal className="col-span-4 row-span-4 img-zoom bg-maly-creamDeep">
              <img src={lookbookImages[0].src} alt="" className="w-full h-full object-cover" />
            </Reveal>
            <Reveal delay={150} className="col-span-2 row-span-3 img-zoom bg-maly-creamDeep">
              <img src={lookbookImages[1].src} alt="" className="w-full h-full object-cover" />
            </Reveal>
            <Reveal delay={250} className="col-span-2 row-span-3 img-zoom bg-maly-creamDeep">
              <img src={lookbookImages[2].src} alt="" className="w-full h-full object-cover" />
            </Reveal>
            <Reveal delay={350} className="col-span-4 row-span-2 img-zoom bg-maly-creamDeep">
              <img src={lookbookImages[3].src} alt="" className="w-full h-full object-cover" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* === VALUES === */}
      <section data-testid="values-section" className="py-32 lg:py-40 bg-maly-navy text-maly-cream px-6 lg:px-12 relative grain">
        <div className="max-w-[1400px] mx-auto relative">
          <Reveal>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-60 mb-6">
              {t.valuesSection.overline}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.01em] font-light max-w-3xl">
              {t.valuesSection.headline}
            </h2>
          </Reveal>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-12 mt-20 lg:mt-28">
            {t.valuesSection.values.map((v, i) => (
              <Reveal key={i} delay={i * 100}>
                <div>
                  <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-gold mb-5">0{i + 1}</div>
                  <h3 className="font-display text-2xl lg:text-3xl mb-4 font-light">{v.title}</h3>
                  <p className="font-body text-sm opacity-70 leading-relaxed">{v.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* === JOURNAL PREVIEW === */}
      <section data-testid="journal-preview" className="py-32 lg:py-40 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
            <div>
              <Reveal>
                <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-6">
                  {t.journalPreview.overline}
                </div>
              </Reveal>
              <Reveal delay={100}>
                <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-[-0.01em] leading-[1.02] font-light max-w-2xl">
                  {t.journalPreview.headline}
                </h2>
              </Reveal>
            </div>
            <Reveal delay={200}>
              <Link to="/journal" data-testid="journal-view-all" className="font-body text-[11px] tracking-[0.25em] uppercase font-medium link-underline whitespace-nowrap">
                {t.journalPreview.cta} →
              </Link>
            </Reveal>
          </div>

          <div className="grid md:grid-cols-3 gap-10 lg:gap-14">
            {journalArticles[lang].slice(0, 3).map((a, i) => (
              <Reveal key={i} delay={i * 100}>
                <Link to="/journal" className="group block">
                  <div className="aspect-[4/5] bg-maly-creamDeep img-zoom mb-6">
                    <img src={a.image} alt={a.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                  <div className="font-body text-[10px] tracking-[0.3em] uppercase text-maly-gold mb-3">{a.tag} · {a.date}</div>
                  <h3 className="font-display text-2xl leading-snug group-hover:opacity-70 transition-opacity">{a.title}</h3>
                  <p className="font-body text-sm opacity-65 mt-3 leading-relaxed">{a.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* === COMMUNITY === */}
      <section data-testid="community-section" className="py-32 lg:py-40 px-6 lg:px-12 bg-maly-creamDeep/40">
        <div className="max-w-[1600px] mx-auto">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <Reveal>
              <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-6">
                {t.community.overline}
              </div>
            </Reveal>
            <Reveal delay={100}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-[-0.01em] font-light">
                {t.community.headline}
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <p className="font-body text-base opacity-65 mt-6 leading-relaxed">{t.community.sub}</p>
            </Reveal>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 lg:gap-4">
            {communityImages.map((img, i) => (
              <Reveal key={i} delay={i * 60} className="aspect-square img-zoom bg-maly-creamDeep">
                <img src={img} alt={`Community ${i+1}`} className="w-full h-full object-cover" loading="lazy" />
              </Reveal>
            ))}
          </div>

          <div className="text-center mt-14">
            <Link to="/community" data-testid="community-cta" className="btn-secondary">
              {t.community.cta} <ChevronRight size={14} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
